/* eslint-disable camelcase */

import axios from 'axios';
import { curry, get } from 'lodash';
import {
    SET_CHECKOUT_PAYMENT_VALID,
    SET_USER_CREDITCARDINFO,
    SET_USER_FAVORITE_PRODUCTS,
    SET_USER_FETCHING_CREDITCARDINFO,
    SET_USER_LAST_PURCHASED_PRODUCTS,
    SET_USER_MOST_PURCHASED_PRODUCTS,
    SET_USER_REPLACEMENT_PRODUCTS_PREFERENCES,
} from 'reducers/actions-types';
import PROTECTED_PAGES from 'constants/protected-pages';

import {
    lastPurchasedProducs,
    login as loginURL,
    logout as logoutURL,
    mostPurchasedProducts,
    replacementProducts,
    userCreditCard,
    userFavoriteProducts,
} from 'constants/api-urls';

import { dispatchError } from 'reducers/helpers';
import productsFactoryMiddleware from 'util/misc/products-factory-middleware';
import { t } from 'translations';


export function removeCard() {
    return function(dispatch) {
        dispatch({ type: SET_USER_CREDITCARDINFO, creditCardInfo: null });
    };
}

/**
 * Fetch credit card info
 * @return {function(*=): Promise<AxiosResponse<any> | never>}
 */
export function fetchCreditCard() {
    return function(dispatch) {
        dispatch({ type: SET_USER_FETCHING_CREDITCARDINFO, isFetchingCreditCardInfo: true });

        return axios
            .get(userCreditCard())
            .then(
                /**
                 * @param {ICreditCard}data
                 */
                ({ data }) => {
                    dispatch({ type: SET_USER_CREDITCARDINFO, creditCardInfo: data });
                    dispatch({ type: SET_USER_FETCHING_CREDITCARDINFO, isFetchingCreditCardInfo: false });
                    dispatch({ type: SET_CHECKOUT_PAYMENT_VALID, isPaymentMethodValid: !!data });
                })
            .catch((e) => {
                dispatch({ type: SET_USER_FETCHING_CREDITCARDINFO, isFetchingCreditCardInfo: false });
                dispatch({ type: SET_CHECKOUT_PAYMENT_VALID, isPaymentMethodValid: false });

                if (e.status && e.status === 404) {
                    dispatch({ type: SET_USER_CREDITCARDINFO, creditCardInfo: null });
                    return null;
                }

                dispatchError(dispatch, e, {
                    url: userCreditCard(),
                    description: e,
                });
                return null;
            });
    };
}

/**
 * Fetch replacement product references
 * @return {function(*=): Promise<AxiosResponse<any> | never>}
 */
export function fetchReplacementProductPreferences() {
    return function(dispatch) {
        return axios
            .get(replacementProducts())
            .then(
                /**
                 * @param {IWantsReplacementsProductsResponse} data
                 */
                ({ data }) => {
                    dispatch({ type: SET_USER_REPLACEMENT_PRODUCTS_PREFERENCES, replacementProductsPreferences: data });
                })
            .catch(e => {
                dispatchError(dispatch, e, {
                    title: t('error.unable_to_fetch_replacement_product_preferences'),
                    url: replacementProducts(),
                    description: e,
                });
            });
    };
}

/**
 * Set replacement product ref
 * @param {boolean} wantsReplacementProducts
 * @return {function(*=, *): Promise<AxiosResponse<any> | never>}
 */
export function setReplacementProductsPreferences({ wantsReplacementProducts }) {
    return function(dispatch, getState) {
        const { user } = getState();
        const isOptionActive = value => value === 'on';
        const oldReplacementProductsPreferences = user.replacementProductsPreferences;

        dispatch({ type: SET_USER_REPLACEMENT_PRODUCTS_PREFERENCES, wantsReplacementProducts: null });

        return axios
            .post(replacementProducts(), {
                wants_replacement_products: isOptionActive(wantsReplacementProducts),
            })
            .then(({ data }) => {
                dispatch({ type: SET_USER_REPLACEMENT_PRODUCTS_PREFERENCES, replacementProductsPreferences: data });
            })
            .catch((e) => {
                dispatchError(dispatch, e, {
                    title: t('error.unable_to_set_replacement_product_preferences'),
                    url: replacementProducts(),
                    description: e,
                });

                dispatch({
                    type: SET_USER_REPLACEMENT_PRODUCTS_PREFERENCES,
                    replacementProductsPreferences: oldReplacementProductsPreferences,
                });
            });
    };
}

/**
 * @param {string} redirectToUrl
 * @return {Function}
 */
export function login(redirectToUrl) {
    return function() {
        window.location.href = loginURL(redirectToUrl);
    };
}

export function logout() {
    return function(dispatch, getState) {
        const { name } = getState().router;
        window.location.href = logoutURL(PROTECTED_PAGES.includes(name) ? window.location.origin : window.location.href);
    };
}

/**
 * Fetch the user specific products and dispatch an event with the products retrieved
 * @param {string} url - url where we will fetch the products
 * @param {string} eventType - event name to dispatch
 * @param {function} dispatch - retux dispatcher function
 * @returns {Function}
 */
function fetchProducts(url, eventType, dispatch, getState) {
    const cooperative = get(getState(), 'region.active.cooperative');

    return axios
        .get(url)
        .then(({ data }) => productsFactoryMiddleware(data.products, { cooperative }))
        .then(
            /**
             * @param {IProduct[] | ICooperativeProduct[]} products
             */
            products => {
                dispatch({ type: eventType, products });
            })
        .catch(e => {
            dispatch({ type: eventType, products: [] });
            // TODO: This error is silenced due to weird responses.
            // dispatchError(dispatch, e, {
            //     url: userFavoriteProducts(),
            //     title: 'Unable to get favorite products',
            //     description: e,
            // });
            return Promise.reject(e);
        });
}

/**
 *
 * @param {string} url
 * @param {number} offset
 * @param {number} limit
 * @return {function(*, *): Promise<IProduct[] | ICooperativeProduct[] | never>}
 */
function fetchProductsBatch(url, offset, limit) {
    return function(dispatch, getState) {
        const cooperative = get(getState(), 'region.active.cooperative');

        return axios
            .get(url, { params: { offset, limit } })
            .then(({ data }) => productsFactoryMiddleware(data.products, { cooperative }))
            .then(
                /**
                 * @param {IProduct[] | ICooperativeProduct[]} products
                 */
                products => {
                    return products;
                });
    };
}

export const populateFavoriteProducts = curry(fetchProducts)(userFavoriteProducts(), SET_USER_FAVORITE_PRODUCTS);
export const fetchMostPurchasedProductsBatch = curry(fetchProductsBatch)(mostPurchasedProducts());
export const fetchLatestPurchasedProductsBatch = curry(fetchProductsBatch)(lastPurchasedProducs());
export const populateMostPurchasedProducts = curry(fetchProducts)(mostPurchasedProducts(), SET_USER_MOST_PURCHASED_PRODUCTS);
export const populateLastPurchasedProducts = curry(fetchProducts)(lastPurchasedProducs(), SET_USER_LAST_PURCHASED_PRODUCTS);




// WEBPACK FOOTER //
// ./mcart/assets/src/js/app/reducers/modules/user/helpers.js